import { HttpParams } from '@angular/common/http';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  CurrentUserSelectors,
  LanguageSelectors,
  RouterSelectors,
  Scopes,
  selectFeatureEnabled,
  UserListSelectors,
} from '@ninety/mobile/platform/state';

import { DiscussionFormalQuestion, DiscussionType } from '../models/discussion.models';
import { DiscussionFormMode } from '../models/form/discussion-form.model';
import DiscussionListVm from '../models/view/discussion-list-container.model';
import { DiscussionListEmptyStateViewModel } from '../models/view/discussion-list-empty-state.model';

import { DiscussionFilterFields } from './actions';
import { InitialListState, selectAll } from './reducers/list.reducers';
import { DiscussionFeatureState, DiscussionFeatureStateKey, InitialDiscussionFeatureState } from './state';

export const _selectFeatureState = createFeatureSelector<DiscussionFeatureState>(DiscussionFeatureStateKey);
export const selectFeatureState = createSelector(_selectFeatureState, state => state ?? InitialDiscussionFeatureState);
export const selectListState = createSelector(selectFeatureState, state => state.list);

export const selectTotalDiscussionCount = createSelector(selectListState, state => state.totalDiscussions);

/** Returns a {@link DiscussionType} based on the current route */
export const selectDiscussionType = createSelector(
  RouterSelectors.selectMergedRoute,
  route => (route.data?.['discussionType'] as DiscussionType) ?? DiscussionType.quarterly
);

/** Returns the `id` of the selected {@link DiscussionListItem} based on the current route. */
export const selectActiveDiscussionId = createSelector(
  RouterSelectors.selectMergedRoute,
  route => (route?.params?.['discussionId'] ?? null) as string
);

export const selectActiveDiscussion = createSelector(selectActiveDiscussionId, selectListState, (id, list) => {
  return list.entities[id] ?? null;
});

/***
 * Based on the selected {@link DiscussionType}, returns plural {@link CompanyLanguage} for
 * feedback items or some default language if no `CompanyLanguage` exists.
 *
 * For instance, if the current `DiscussionType` is `quarterly`, the output may be something like: `Quarterly Discussions`
 */
export const selectPluralLanguageByDiscussionType = createSelector(
  LanguageSelectors.discussionLanguage,
  selectDiscussionType,
  (lang, discussionType) => {
    const langMap: Record<DiscussionType, string> = {
      [DiscussionType.quarterly]: lang?.quarterlyItems,
      [DiscussionType.annual]: lang?.annualItems,
    };
    return langMap[discussionType ?? DiscussionType.quarterly];
  }
);

export const selectAllDiscussions = createSelector(selectListState, selectAll);
export const selectAllDiscussionsLoaded = createSelector(selectListState, state => state.allDiscussionsLoaded);
export const selectLoadingDiscussions = createSelector(selectListState, state => state.loadingDiscussionList);
export const selectPager = createSelector(selectListState, state => state.pager);
export const selectSort = createSelector(selectListState, state => state.sort);
export const selectFilter = createSelector(selectListState, state => state.filter);
export const selectFilterChips = createSelector(
  selectFilter,
  UserListSelectors.selectActiveUsers,
  CurrentUserSelectors.selectUserId,
  (filters, users, currentUserId) => {
    const chips: { field: DiscussionFilterFields; key: string; value?: string }[] = [];
    const defaultUserId = currentUserId ?? InitialListState.filter.selectedUserId;

    if (filters.selectedUserId !== defaultUserId) {
      chips.push({
        field: 'selectedUserId',
        key: 'User',
        value:
          filters.selectedUserId === InitialListState.filter.selectedUserId
            ? 'All'
            : (users.find(u => u._id === filters.selectedUserId)?.name ?? 'Unknown'),
      });
    }

    if (filters.showCompleted !== InitialListState.filter.showCompleted) {
      chips.push({
        field: 'showCompleted',
        key: 'Completed',
      });
    }

    return chips;
  }
);

export namespace DiscussionFormSelectors {
  export const selectFormState = createSelector(selectFeatureState, state => state.form);
  export const selectFormDiscussion = createSelector(selectFormState, state => state.discussion);
  export const selectFormIsLoading = createSelector(selectFormState, state => state.loading);
  export const selectFormIsSaving = createSelector(selectFormState, state => state.saving);

  /** Determines what {@link DiscussionFormMode} the form should be presented in. */
  export const selectFormMode = createSelector(
    selectFormDiscussion,
    CurrentUserSelectors.selectUserId,
    RouterSelectors.selectMergedRoute,
    (discussion, userId, route) => {
      if (!discussion) return DiscussionFormMode.None;

      const isFormRoute = route.data?.['form'];
      const isMeetingRoute = route.data?.['meeting'];
      const isManager = userId === discussion.managerUserId;
      const isManagee = userId === discussion.manageeUserId;
      const isParticipant = isManager || isManagee;

      if (isFormRoute && isManager) return DiscussionFormMode.Manager;
      if (isFormRoute && isManagee) return DiscussionFormMode.Managee;
      if (isMeetingRoute && isParticipant) return DiscussionFormMode.Meeting;

      return DiscussionFormMode.None;
    }
  );

  export const selectFormIsReadOnly = createSelector(selectFormState, selectFormMode, ({ discussion }, mode) => {
    if (!discussion) return true;

    const { isManagerDone, isManageeDone } = discussion;

    if (mode === DiscussionFormMode.Manager) return !!isManagerDone;
    if (mode === DiscussionFormMode.Managee) return !!isManageeDone;
    // This can be used when we allow discussion meetings in the mobile app
    // if (mode === DiscussionFormMode.Meeting) return !!isManagerDone && !!isManageeDone && !!isCompleted;

    return true;
  });

  export const selectDisplayManageeFormSection = createSelector(
    selectFormState,
    selectFormMode,
    (state, mode) => mode === DiscussionFormMode.Manager || !!state.discussion?.canEvaluateSelf
  );
  export const selectDisplayManagerFormSection = createSelector(
    selectFormState,
    selectFormMode,
    (state, mode) => mode === DiscussionFormMode.Manager || !!state.discussion?.canEvaluateManager
  );
  export const selectFormQuestions = createSelector(
    selectFormState,
    (state): DiscussionFormalQuestion[] =>
      (state.discussion?.type === DiscussionType.quarterly
        ? state.discussion?.quarterlyQuestions
        : state.discussion?.meetingQuestions) ?? []
  );
  export const selectStepperAndFormData = createSelector(
    selectFormDiscussion,
    selectFormMode,
    selectFormQuestions,
    selectDisplayManagerFormSection,
    selectDisplayManageeFormSection,
    selectFormIsReadOnly,
    (discussion, mode, questions, displayManagerFormSection, displayManageeFormSection, readonly) => ({
      discussion,
      mode,
      questions,
      displayManagerFormSection,
      displayManageeFormSection,
      readonly,
    })
  );
}

export const selectGetDiscussionListQueryParams = createSelector(
  selectDiscussionType,
  selectPager,
  selectSort,
  selectFilter,
  (type, { page, take }, { sortField, sortDirection }, { selectedUserId, showCompleted }) =>
    new HttpParams({
      fromObject: {
        type,
        page,
        take,
        sortField,
        sortDirection,
        selectedUserId,
        isCompleted: showCompleted,
      },
    })
);

/*****************************************
 *         View Model Selectors          *
 *****************************************/
export const selectDiscussionListVm = createSelector(
  selectAllDiscussions,
  selectLoadingDiscussions,
  selectPager,
  CurrentUserSelectors.selectUserId,
  selectFilter,
  selectFeatureEnabled(Scopes.oneOnOne),
  (discussions, loading, pager, currentUserId, filter, hasFeatureAccess): DiscussionListVm => {
    return {
      discussions,
      loading,
      pager,
      currentUserId: currentUserId ?? 'all',
      isShowingCompleted: filter.showCompleted,
      hasFeatureAccess,
    };
  }
);

export const selectDiscussionListEmptyStateVm = createSelector(
  LanguageSelectors.discussionLanguage,
  selectPluralLanguageByDiscussionType,
  (lang, pluralItemsByType): DiscussionListEmptyStateViewModel => {
    const item = lang?.item ?? 'Discussion';
    const items = lang?.items ?? 'Discussions';
    return {
      title: `You don't have any ${pluralItemsByType ?? items}.`,
      description: `${items} are a great way to give and receive feedback from your teammates.`,
      cta: `Create ${item}`,
      learnMoreText: `Learn more about ${items}.`,
      learnMoreUrl: 'https://www.ninety.io/features/1-on-1s',
    };
  }
);

export const selectDiscussionUniversalCreateButtonText = createSelector(
  LanguageSelectors.discussionLanguage,
  lang => `Create ${lang?.item ?? 'Discussion'}`
);
